/**
 * Shared product/variant pricing logic — used by the storefront (PDP + drawer
 * + buy box), the server checkout validation, and the JSON-LD/feed builders so
 * every surface agrees on what a product costs and whether it's purchasable.
 *
 * Pricing model: a variant's effective price is its own `pricePence`, falling
 * back to the product's base price when the variant leaves it blank. A product
 * can therefore be priced EITHER by a base price OR purely by its variants —
 * the base price is not required when variants carry the pricing.
 */

export interface PricedVariant {
  pricePence: number | null;
}

/**
 * Effective price of a (possibly null) selected variant against the product's
 * base price: `variant.pricePence ?? basePricePence`. Returns the base price
 * when no variant is supplied, and null when neither has a price.
 */
export function resolveVariantPrice(
  variant: { pricePence: number | null } | null | undefined,
  basePricePence: number | null
): number | null {
  if (!variant) return basePricePence;
  return variant.pricePence ?? basePricePence;
}

/**
 * Min/max sellable price across a product's options. With variants, the range
 * spans each variant's resolved price (variant price, else base); without
 * variants it's just the base price. Returns nulls when nothing is priceable.
 */
export function variantPriceRange(
  basePricePence: number | null,
  variants: PricedVariant[]
): { min: number | null; max: number | null } {
  const prices: number[] = [];
  if (variants.length > 0) {
    for (const v of variants) {
      const p = v.pricePence ?? basePricePence;
      if (typeof p === "number") prices.push(p);
    }
  } else if (typeof basePricePence === "number") {
    prices.push(basePricePence);
  }
  if (prices.length === 0) return { min: null, max: null };
  return { min: Math.min(...prices), max: Math.max(...prices) };
}

/**
 * Sort/compare key for listing by price: a product's LOWEST sellable price (its
 * "from" price), or null when nothing is priceable ("Enquire"). Variant-priced
 * products (null base + priced variants) sort by their cheapest variant — never
 * by the null base price. Sorting on the bare base `pricePence` was the
 * 2026-07 admin bug: every variant-priced product collapsed to one value and
 * clumped together instead of ordering by real price.
 */
export function productFromPrice(
  basePricePence: number | null,
  variants: PricedVariant[]
): number | null {
  return variantPriceRange(basePricePence, variants).min;
}

/**
 * Whether a product can be bought self-service: it must not be flagged
 * enquire-only AND a price must resolve from the base price or any variant.
 */
export function isPurchasable(
  deliveryTiming: string | null | undefined,
  basePricePence: number | null,
  variants: PricedVariant[]
): boolean {
  if (deliveryTiming === "enquireOnly") return false;
  return variantPriceRange(basePricePence, variants).min != null;
}
